(function () {
  'use strict';

  const DESKTOP_MQ = '(min-width: 700px)';
  const discountCache = new WeakMap();
  const initialStateCache = new WeakMap();

  function decodeCode(raw) {
    if (!raw) return '';
    try { return atob(raw); } catch (e) { return raw; }
  }

  function getCartRoot() {
    return (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
  }

  function fetchStorefrontDiscountPrice(variantId, code) {
    if (!window.StorefrontDiscountPrice || !window.StorefrontDiscountPrice.fetchSaleCents) {
      return Promise.reject(new Error('Storefront discount helper missing'));
    }
    return window.StorefrontDiscountPrice.fetchSaleCents(variantId, code);
  }

  function applyDiscountCode(code) {
    if (!code) return Promise.resolve(null);
    return fetch(getCartRoot() + 'cart/update.js', {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ discount: code }),
    })
      .then(function (response) {
        if (!response.ok) throw new Error('discount');
        return response.json();
      })
      .catch(function () {
        return null;
      });
  }

  function clearDiscount() {
    return fetch(getCartRoot() + 'cart/update.js', {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ discount: '' }),
    }).catch(function () {});
  }

  function anyOtherRevealed(exceptBlock) {
    return Array.from(document.querySelectorAll('.pcp-block--revealed[data-pcp-id]')).some(
      function (block) {
        return block !== exceptBlock;
      }
    );
  }

  function formatMoney(cents, format) {
    if (typeof cents === 'string') cents = cents.replace('.', '');
    cents = Number(cents);
    if (Number.isNaN(cents)) return '';

    const settings = window.themeVariables?.settings || {};
    const formatString =
      format ||
      (settings.currencyCodeEnabled
        ? settings.moneyWithCurrencyFormat
        : settings.moneyFormat) ||
      '{{amount}}';

    const placeholderRegex = /\{\{\s*(\w+)\s*\}\}/;

    function formatWithDelimiters(number, precision, thousands, decimal) {
      number = (number / 100).toFixed(precision ?? 2);
      const parts = number.split('.');
      const whole = parts[0].replace(/(\d)(?=(\d\d\d)+(?!\d))/g, '$1' + (thousands ?? ','));
      const fraction = parts[1] ? (decimal ?? '.') + parts[1] : '';
      return whole + fraction;
    }

    const match = formatString.match(placeholderRegex);
    const token = match ? match[1] : 'amount';
    let value = '';

    switch (token) {
      case 'amount_no_decimals':
        value = formatWithDelimiters(cents, 0);
        break;
      case 'amount_with_space_separator':
        value = formatWithDelimiters(cents, 2, ' ', '.');
        break;
      case 'amount_with_comma_separator':
        value = formatWithDelimiters(cents, 2, '.', ',');
        break;
      case 'amount_with_apostrophe_separator':
        value = formatWithDelimiters(cents, 2, "'", '.');
        break;
      case 'amount_no_decimals_with_comma_separator':
        value = formatWithDelimiters(cents, 0, '.', ',');
        break;
      case 'amount_no_decimals_with_space_separator':
        value = formatWithDelimiters(cents, 0, ' ');
        break;
      case 'amount_no_decimals_with_apostrophe_separator':
        value = formatWithDelimiters(cents, 0, "'");
        break;
      default:
        value = formatWithDelimiters(cents, 2);
    }

    return formatString.replace(placeholderRegex, value);
  }

  function fromLabel(template, price) {
    return template ? template.replace('@@', price) : price;
  }

  function getBlockControls(block) {
    return {
      checkbox: block.querySelector('.pcp-reveal-checkbox'),
      button: block.querySelector('.pcp-reveal-btn'),
    };
  }

  function getBlockControl(block) {
    const { checkbox, button } = getBlockControls(block);
    return checkbox || button;
  }

  function isMembersOnlyBlock(block) {
    return !!block.closest('.members-only-products');
  }

  function autoRevealMembersOnlyBlocks(root) {
    const scope = root || document;
    scope.querySelectorAll('.members-only-products .pcp-block[data-pcp-id]').forEach(function (block) {
      if (
        block.classList.contains('pcp-block--revealed') ||
        block.classList.contains('pcp-block--loading') ||
        block.dataset.pcpAutoRevealAttempted === '1'
      ) {
        return;
      }

      const control = getBlockControl(block);
      if (!control) return;

      block.dataset.pcpAutoRevealAttempted = '1';
      block.classList.add('pcp-block--auto-reveal');
      revealBlock(block, control);
    });
  }

  function setControlsDisabled(block, disabled) {
    const { checkbox, button } = getBlockControls(block);
    if (checkbox) checkbox.style.cursor = disabled ? 'wait' : '';
    if (button) button.style.cursor = disabled ? 'wait' : '';
  }

  function setControlState(block, revealed) {
    const { checkbox, button } = getBlockControls(block);
    const bar = block.querySelector('.pcp-toggle-bar');

    if (checkbox) checkbox.checked = revealed;

    if (button) {
      button.setAttribute('aria-expanded', revealed ? 'true' : 'false');
      button.setAttribute('aria-checked', revealed ? 'true' : 'false');
      button.classList.toggle('pcp-reveal-btn--active', revealed);
    }

    if (bar) bar.classList.toggle('pcp-toggle-bar--active', revealed);
  }

  function isPriceListMode(block) {
    return (
      block.dataset.pcpMode === 'price-list' ||
      !!block.closest('price-list')
    );
  }

  function getPriceHost(block) {
    return (
      block.closest('price-list') ||
      block.closest('.cts-card__price') ||
      block.parentElement
    );
  }

  function getPriceNodes(block) {
    const host = getPriceHost(block);
    if (!host) return { sale: null, saved: null };

    if (isPriceListMode(block) || host.matches?.('price-list')) {
      return {
        sale: host.querySelector('sale-price'),
        saved: host.querySelector('.pcp-saved'),
      };
    }

    return {
      sale: host.querySelector('.cts-price--current, .cts-price--regular'),
      saved: host.querySelector('.pcp-saved'),
    };
  }

  function cacheInitialState(block) {
    if (initialStateCache.has(block)) return;

    const { sale, saved } = getPriceNodes(block);
    initialStateCache.set(block, {
      saleClass: sale ? sale.className : '',
      saleHtml: sale ? sale.innerHTML : '',
      savedClass: saved ? saved.className : '',
      savedHtml: saved ? saved.innerHTML : '',
      savedExisted: !!saved,
    });
  }

  function setLabeledPrice(el, label, formatted) {
    if (!el) return;
    el.innerHTML =
      (label ? '<span class="sr-only">' + label + '</span>' : '') + formatted;
  }

  function createPriceLoader() {
    const loader = document.createElement('span');
    loader.className = 'pcp-toggle-bar__loader';
    loader.setAttribute('aria-hidden', 'true');
    loader.innerHTML =
      '<span class="pcp-toggle-bar__loader-dot"></span>' +
      '<span class="pcp-toggle-bar__loader-dot"></span>' +
      '<span class="pcp-toggle-bar__loader-dot"></span>';
    return loader;
  }

  function findSalePriceLoader(sale) {
    if (!sale) return null;
    return sale.querySelector(':scope > .pcp-toggle-bar__loader');
  }

  function setSalePriceLoading(block, loading) {
    const { sale } = getPriceNodes(block);
    const host = getPriceHost(block);
    if (host) host.classList.toggle('pcp-price-host--loading', loading);

    if (!sale) return;

    const sibling = sale.nextElementSibling;
    if (sibling && sibling.classList.contains('pcp-toggle-bar__loader')) {
      sibling.remove();
    }

    const existing = findSalePriceLoader(sale);
    if (loading) {
      sale.classList.add('text-on-sale', 'pcp-sale-loading');
      if (!existing) {
        sale.appendChild(createPriceLoader());
      }
    } else {
      sale.classList.remove('pcp-sale-loading');
      if (existing) existing.remove();
    }
  }

  function applyRevealedSaleClass(block, sale) {
    if (!sale) return;

    const saleClass = (block.dataset.pcpSaleClass || 'text-on-sale').trim();
    if (saleClass) {
      sale.className = saleClass;
    } else {
      sale.classList.remove('text-subdued');
      sale.classList.add('text-on-sale');
    }
    sale.classList.remove('pcp-sale-loading');
  }

  /*function updateSavedAmount(block, saleCents, listCents) {
    const { saved } = getPriceNodes(block);
    if (!saved || !saleCents) return;

    const compareCents = parseInt(saved.getAttribute('data-compare-cents'), 10) || 0;
    const baseCents = compareCents > saleCents ? compareCents : listCents || 0;
    const savedCents = baseCents > saleCents ? baseCents - saleCents : 0;
    if (!savedCents) {
      saved.hidden = true;
      return;
    }

    saved.hidden = false;
    saved.textContent = 'Gespart ' + formatMoney(savedCents);
    saved.classList.add('pcp-saved--revealed');
  }*/


  function updateSavedAmount(block, saleCents, listCents) {
    if (!saleCents) return;
  
    const host = getPriceHost(block);
    if (!host) return;
  
    let { sale, saved } = getPriceNodes(block);
    const compareCents = saved
      ? parseInt(saved.getAttribute('data-compare-cents'), 10) || 0
      : 0;
    const baseCents = compareCents > saleCents ? compareCents : listCents || 0;
    const savedCents = baseCents > saleCents ? baseCents - saleCents : 0;
  
    if (!savedCents) {
      if (saved) saved.hidden = true;
      return;
    }
  
    if (!saved) {
      saved = document.createElement('span');
      saved.className = 'pcp-saved';
      saved.setAttribute('data-compare-cents', String(baseCents));
      if (sale) sale.insertAdjacentElement('afterend', saved);
      else host.appendChild(saved);
    }
  
    saved.hidden = false;
    saved.textContent = 'Gespart ' + formatMoney(savedCents);
    saved.classList.add('pcp-saved--revealed');
  }

  function showRevealed(block, control, discountData) {
    cacheInitialState(block);

    const { sale } = getPriceNodes(block);
    if (!sale) return;

    const { saleCents, listCents } = discountData;
    if (!saleCents) {
      block.classList.add('pcp-block--error');
      setControlState(block, false);
      return;
    }

    const varies = control?.dataset.pcpVaries === '1';
    const fromTemplate = control?.dataset.pcpF;
    const saleFormatted = varies
      ? fromLabel(fromTemplate, formatMoney(saleCents))
      : formatMoney(saleCents);
    const saleLabel = control?.dataset.pcpSaleLabel || 'Sale price';
    const priceListMode = isPriceListMode(block) || !!block.closest('price-list');

    if (!saleFormatted) {
      block.classList.add('pcp-block--error');
      setControlState(block, false);
      return;
    }

    if (priceListMode) {
      setLabeledPrice(sale, saleLabel, saleFormatted);
    } else {
      sale.textContent = saleFormatted;
    }

    applyRevealedSaleClass(block, sale);
    updateSavedAmount(block, saleCents, listCents);

    block.classList.add('pcp-block--revealed');
    block.classList.remove('pcp-block--error');
    const host = getPriceHost(block);
    if (host) host.classList.add('pcp-price-host--revealed');
    setControlState(block, true);
  }

  function hideRevealed(block) {
    const cached = initialStateCache.get(block);
    const { sale, saved } = getPriceNodes(block);

    if (cached && sale) {
      sale.className = cached.saleClass;
      sale.innerHTML = cached.saleHtml;
    }

    if (saved) {
      if (cached && cached.savedExisted) {
        saved.className = cached.savedClass;
        saved.innerHTML = cached.savedHtml;
        saved.hidden = false;
      } else {
        saved.remove();
      }
    }

    block.classList.remove('pcp-block--revealed', 'pcp-block--loading', 'pcp-block--error');
    setSalePriceLoading(block, false);
    const host = getPriceHost(block);
    if (host) host.classList.remove('pcp-price-host--revealed');
    setControlState(block, false);
    discountCache.delete(block);

    if (!anyOtherRevealed(block)) clearDiscount();
  }

  async function revealBlock(block, control) {
    if (block.classList.contains('pcp-block--loading')) return;

    const code = decodeCode((control?.dataset.pcpCode || '').trim());
    const variantId = parseInt(control?.dataset.pcpVariantId, 10) || 0;
    if (!code || !variantId) {
      block.classList.add('pcp-block--error');
      setControlState(block, false);
      return;
    }

    cacheInitialState(block);
    block.classList.add('pcp-block--loading');
    setControlsDisabled(block, true);
    setSalePriceLoading(block, true);

    let discountData = discountCache.get(block);

    try {
      if (!discountData) {
        const cartData = await fetchStorefrontDiscountPrice(variantId, code);
        discountData = {
          saleCents: cartData.saleCents,
          listCents: cartData.listCents || 0,
          varies: control.dataset.pcpVaries === '1',
        };
        discountCache.set(block, discountData);
      }
      applyDiscountCode(code);
    } catch (error) {
      block.classList.add('pcp-block--error');
      block.classList.remove('pcp-block--loading');
      setSalePriceLoading(block, false);
      setControlsDisabled(block, false);
      setControlState(block, false);
      return;
    }

    setSalePriceLoading(block, false);
    showRevealed(block, control, discountData);
    block.classList.remove('pcp-block--loading');
    setControlsDisabled(block, false);
  }

  async function toggleBlock(block, button) {
    if (block.classList.contains('pcp-block--loading')) return;

    if (block.classList.contains('pcp-block--revealed')) {
      hideRevealed(block);
      return;
    }

    await revealBlock(block, button);
  }

  function onClick(event) {
    const button = event.target.closest('.pcp-reveal-btn');
    if (!button || button.disabled) return;

    const block = button.closest('.pcp-block');
    if (!block) return;

    event.preventDefault();
    toggleBlock(block, button);
  }

  function onChange(event) {
    const checkbox = event.target;
    if (!checkbox.matches('.pcp-reveal-checkbox') || checkbox.disabled) return;

    const block = checkbox.closest('.pcp-block');
    if (!block) return;

    if (checkbox.checked) {
      if (!block.classList.contains('pcp-block--revealed')) {
        revealBlock(block, checkbox);
      }
    } else if (block.classList.contains('pcp-block--revealed')) {
      hideRevealed(block);
    }
  }

  function syncControlsOnResize() {
    document.querySelectorAll('.pcp-block--revealed[data-pcp-id]').forEach((block) => {
      setControlState(block, true);
    });
  }

  function validateBlock(block) {
    if (block.dataset.pcpValidated === '1') return;
    block.dataset.pcpValidated = '1';

    const control = getBlockControl(block);
    if (!control) {
      block.remove();
      return;
    }

    const code = decodeCode((control.dataset.pcpCode || '').trim());
    const variantId = parseInt(control.dataset.pcpVariantId, 10) || 0;
    if (!code || !variantId) {
      block.remove();
      return;
    }

    block.hidden = false;

    fetchStorefrontDiscountPrice(variantId, code)
      .then(function (cartData) {
        discountCache.set(block, {
          saleCents: cartData.saleCents,
          listCents: cartData.listCents || 0,
          varies: control.dataset.pcpVaries === '1',
        });
        if (
          isMembersOnlyBlock(block) &&
          block.dataset.pcpAutoRevealAttempted !== '1' &&
          !block.classList.contains('pcp-block--revealed')
        ) {
          block.dataset.pcpAutoRevealAttempted = '1';
          block.classList.add('pcp-block--auto-reveal');
          revealBlock(block, control);
        }
      })
      .catch(function () {});
  }

  function initBlocks(root) {
    (root || document).querySelectorAll('.pcp-block[data-pcp-id]').forEach((block) => {
      cacheInitialState(block);
      if (!isMembersOnlyBlock(block)) {
        setControlState(block, false);
      }
      validateBlock(block);
    });
  }

  function init() {
    initBlocks();
    document.addEventListener('click', onClick);
    document.addEventListener('change', onChange);

    if (window.matchMedia) {
      const mq = window.matchMedia(DESKTOP_MQ);
      if (mq.addEventListener) {
        mq.addEventListener('change', syncControlsOnResize);
      } else if (mq.addListener) {
        mq.addListener(syncControlsOnResize);
      }
    }

    if (typeof MutationObserver === 'undefined') return;

    const observer = new MutationObserver((mutations) => {
      for (const mutation of mutations) {
        mutation.addedNodes.forEach((node) => {
          if (node.nodeType !== 1) return;
          if (node.matches?.('.pcp-block[data-pcp-id]')) {
            initBlocks(node.parentElement || node);
          } else if (node.querySelector?.('.pcp-block[data-pcp-id]')) {
            initBlocks(node);
          }
        });
      }
    });

    observer.observe(document.body, { childList: true, subtree: true });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
